import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { Observable, BehaviorSubject } from 'rxjs';
import { Inject } from '@angular/core';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import {
  Company,
  CreateCompanyDto,
  UpdateCompanyDto,
  CatalogsDto,
  ReservationsDto,
  BillingDto,
  PaymentDto,
  SystemDto,
  AppearanceDto,
  CompanyCountResponse
} from '../models/company.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly API_URL = `${environment.apiUrl}/companies`;

  // Subject para manejar el estado de la empresa actual
  private currentCompanySubject = new BehaviorSubject<Company | null>(null);
  public currentCompany$ = this.currentCompanySubject.asObservable();

  constructor(
    private http: HttpClient,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document
  ) {}

  // CRUD Básico

  /**
   * Crear una nueva empresa
   */
  create(companyDto: CreateCompanyDto): Observable<Company> {
    return this.http.post<Company>(this.API_URL, companyDto)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Obtener todas las empresas
   */
  findAll(activeOnly?: boolean): Observable<Company[]> {
    let params = new HttpParams();
    if (activeOnly !== undefined) {
      params = params.set('active', activeOnly.toString());
    }

    return this.http.get<Company[]>(this.API_URL, { params });
  }

  /**
   * Obtener empresa por ID
   */
  findOne(id: string): Observable<Company> {
    return this.http.get<Company>(`${this.API_URL}/${id}`)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Buscar empresa por email
   */
  findByEmail(email: string): Observable<Company> {
    return this.http.get<Company>(`${this.API_URL}/email/${email}`)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }


  /**
   * Obtener conteo de empresas
   */
  getCount(activeOnly?: boolean): Observable<CompanyCountResponse> {
    let params = new HttpParams();
    if (activeOnly !== undefined) {
      params = params.set('active', activeOnly.toString());
    }

    return this.http.get<CompanyCountResponse>(`${this.API_URL}/count`, { params });
  }

  /**
   * Actualizar empresa
   */
  update(id: string, updateDto: UpdateCompanyDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}`, updateDto)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Eliminación permanente
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`)
      .pipe(
        tap(() => {
          // Si era la empresa actual, limpiar el subject
          if (this.currentCompanySubject.value?._id === id) {
            this.currentCompanySubject.next(null);
          }
        })
      );
  }

  // Soft Delete y Restore

  /**
   * Eliminación lógica (desactivar)
   */
  softDelete(id: string): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/soft-delete`, {})
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Restaurar empresa (reactivar)
   */
  restore(id: string): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/restore`, {})
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  // Configuraciones Específicas

  /**
   * Actualizar configuraciones de reservas
   */
  /**
   * Actualiza los catalogos de tipos de clase e instructores. Solo viaja el
   * catalogo que se pase; el backend normaliza nombres y duplicados.
   */
  updateCatalogs(id: string, catalogs: CatalogsDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/catalogs`, catalogs)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  updateReservations(id: string, settings: ReservationsDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/reservations`, settings)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Actualizar configuraciones de facturación
   */
  updateBilling(id: string, settings: BillingDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/billing`, settings)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Actualizar configuraciones de pago (políticas + métodos)
   */
  updatePayment(id: string, settings: PaymentDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/payment`, settings)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Actualizar características del sistema
   */
  updateSystem(id: string, settings: SystemDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/system`, settings)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  /**
   * Actualizar configuraciones de apariencia
   */
  updateAppearance(id: string, settings: AppearanceDto): Observable<Company> {
    return this.http.patch<Company>(`${this.API_URL}/${id}/appearance`, settings)
      .pipe(
        tap(company => this.currentCompanySubject.next(company))
      );
  }

  // Métodos de utilidad

  /**
   * Obtener la empresa actual del subject
   */
  getCurrentCompany(): Company | null {
    return this.currentCompanySubject.value;
  }

  /**
   * Establecer la empresa actual
   */
  setCurrentCompany(company: Company | null): void {
    this.currentCompanySubject.next(company);
  }

  /**
   * Cargar empresa por dominio actual o la primera empresa activa como fallback
   */
  loadDefaultCompany(): Observable<Company[]> {
    // Primero intentar obtener empresa por dominio
    return this.getCompanyByDomain().pipe(
      switchMap(companyInfo => {
        // Si encontramos empresa por dominio, buscar la empresa completa en la lista de empresas activas
        return this.findAll(true).pipe(
          tap(companies => {
            const foundCompany = companies.find(company => company.name === companyInfo.name);
            if (foundCompany) {
              this.currentCompanySubject.next(foundCompany);
            } else if (companies.length > 0) {
              // Si no encontramos la empresa específica, usar la primera activa
              this.currentCompanySubject.next(companies[0]);
            }
          })
        );
      }),
      // En caso de error (no se encuentra por dominio), usar findAll como fallback
      catchError(() => {
        return this.loadFallbackCompany();
      })
    );
  }

  /**
   * Cargar la primera empresa activa como fallback
   */
  private loadFallbackCompany(): Observable<Company[]> {
    return this.findAll(true).pipe(
      tap(companies => {
        if (companies.length > 0) {
          this.currentCompanySubject.next(companies[0]);
        }
      })
    );
  }

  /**
   * Verificar si hay una empresa configurada
   */
  hasCompany(): Observable<boolean> {
    return this.getCount(true).pipe(
      map(response => response.count > 0)
    );
  }

  /**
   * Obtener información básica de empresa por dominio actual
   */
  getCompanyByDomain(): Observable<{ name: string; logo?: string; favicon?: string }> {
    return this.http.get<{ name: string; logo?: string; favicon?: string }>(`${this.API_URL}/by-domain`)
      .pipe(
        tap(companyInfo => {
          // Actualizar el título de la página con el nombre de la empresa
          this.titleService.setTitle(companyInfo.name);

          // Actualizar el favicon si está disponible
          if (companyInfo.favicon) {
            this.updateFavicon(companyInfo.favicon);
          }
        })
      );
  }

  /**
   * Actualizar el favicon de la página
   */
  private updateFavicon(faviconData: string): void {
    // Buscar el elemento link del favicon existente
    let faviconLink = this.document.querySelector('link[rel="icon"]') as HTMLLinkElement;

    if (!faviconLink) {
      // Si no existe, crear uno nuevo
      faviconLink = this.document.createElement('link');
      faviconLink.rel = 'icon';
      this.document.head.appendChild(faviconLink);
    }

    // Actualizar el href con el data URL del favicon
    faviconLink.href = faviconData;
    faviconLink.type = 'image/png'; // Asumiendo que es PNG, pero podríamos detectarlo del data URL
  }

  /**
   * Limpiar la empresa actual
   */
  clearCurrentCompany(): void {
    this.currentCompanySubject.next(null);
  }
}